import React from 'react';
import { fieldProps } from './missingFields';
import { StepHeader, StepNote } from './StepHeader';
import CountrySelect from './CountrySelect';
import GstinVerifyPanel from './GstinVerifyPanel';
import EmailOtpPanel from './EmailOtpPanel';
import Icon from '../../../components/AppIcon';
import { isIndia } from '../../../utils/countries';

/**
 * Step 1 — who you are. Country first; India unlocks the GSTIN flow, anything else continues
 * to the manual steps. Email ownership is proven here for everyone.
 */
const IdentityStep = ({
  formData,
  errors,
  sessionId,
  identity,
  kyc,
  onCountryChange,
  onGstinVerified,
  emailVerificationRequired,
  emailVerified,
  onEmailVerified,
  countryBusy = false,
}) => {
  const india = isIndia(formData.countryOrigin);
  const verified = Boolean(identity?.verified_at);

  return (
    <div className="space-y-6">
      <StepHeader
        title="Identify your business"
        description="Pick your country, verify your business registration, and confirm your email. Everything else in this form builds on these three facts."
      >
        {india && !verified && (
          <StepNote icon="Sparkles">
            Indian vendors: one GSTIN lookup pre-fills most of the remaining steps. Fetched values are shown read-only;
            you only fill what no registry holds.
          </StepNote>
        )}
      </StepHeader>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="md:col-span-2 md:max-w-md">
          <CountrySelect
            value={formData.countryOrigin}
            triggerProps={fieldProps('countryOrigin')}
            onChange={onCountryChange}
            error={errors.countryOrigin}
            disabled={countryBusy || verified}
            description={verified ? 'Locked while a GSTIN is verified.' : 'Where the business is registered.'}
          />
          {verified && (
            <button
              type="button"
              onClick={() => onCountryChange(null)}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
            >
              <Icon name="RotateCcw" size={12} /> Change country or GSTIN
            </button>
          )}
        </div>

        {india && (
          <div className="md:col-span-2 outline-none" {...fieldProps('gstinVerified')} tabIndex={-1}>
            <GstinVerifyPanel sessionId={sessionId} identity={identity} kyc={kyc} onVerified={onGstinVerified} />
            {errors.gstinVerified && !verified && (
              <p className="mt-2 text-sm text-destructive">{errors.gstinVerified}</p>
            )}
          </div>
        )}

        {formData.countryOrigin && (
          <>
            <div className="space-y-2 md:col-span-2 md:max-w-md">
              <label htmlFor="registration-email" className="text-sm font-medium leading-none text-foreground">
                SPOC Email Address <span className="text-destructive ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  id="registration-email"
                  type="email"
                  value={formData.email || ''}
                  readOnly
                  aria-readonly="true"
                  aria-describedby="registration-email-help"
                  className="flex h-10 w-full rounded-md border border-border bg-muted/60 pl-3 pr-10 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 cursor-default"
                />
                <span
                  className={`pointer-events-none absolute inset-y-0 right-3 flex items-center ${
                    emailVerified ? 'text-success' : 'text-muted-foreground'
                  }`}
                  aria-hidden="true"
                >
                  <Icon name={emailVerified ? 'ShieldCheck' : 'Lock'} size={16} />
                </span>
              </div>
              <p id="registration-email-help" className="text-sm text-muted-foreground">
                Locked to the address your invitation was sent to.
              </p>
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>
            {emailVerificationRequired && (
              <div className="md:col-span-2 outline-none" {...fieldProps('emailVerified')} tabIndex={-1}>
                <EmailOtpPanel
                  sessionId={sessionId}
                  email={formData.email}
                  verified={emailVerified}
                  onVerified={onEmailVerified}
                />
                {errors.emailVerified && !emailVerified && (
                  <p className="mt-2 text-sm text-destructive">{errors.emailVerified}</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default IdentityStep;
